<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>Bills By Subject</h1>
        <router-link to="/subject/new" class="add-btn">
          <span class="btn-icon">+</span>
          New Subject
        </router-link>
      </div>

      <DataTable 
        :value="filteredBills" 
        :paginator="true" 
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        :loading="loading"
        stripedRows
        showGridlines
        responsiveLayout="scroll"
        :globalFilterFields="['subject_title', 'period_of_congress', 'tier', 'urgency']"
        v-model:filters="filters"
        filterDisplay="menu"
      >
        <template #header>
          <div class="table-header">
            <h3>Bills By Subject ({{ filteredBills.length }})</h3>
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search bills..." />
            </IconField>
          </div>
        </template>
        
        <Column field="subject_title" header="Subject Title" sortable :showFilterMatchModes="false">
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search title" />
          </template>
        </Column>
        
        <Column field="period_of_congress" header="Congress" sortable :showFilterMatchModes="false">
          <template #body="{ data }">
            {{ data.period_of_congress || 'N/A' }}
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search congress" />
          </template>
        </Column>
        
        <Column field="tier" header="Tier" sortable :showFilterMatchModes="false">
          <template #body="{ data }">
            <span class="tier-badge" :class="getTierClass(data.tier)">{{ data.tier }}</span>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search tier" />
          </template>
        </Column>
        
        <Column field="subject_type" header="Type" sortable>
          <template #body="{ data }">
            {{ getSubjectTypeDisplay(data.subject_type) }}
          </template>
        </Column>
        
        <Column field="urgency" header="Urgency" sortable :showFilterMatchModes="false">
          <template #body="{ data }">
            <span class="urgency-badge" :class="getUrgencyClass(data.urgency)">{{ data.urgency }}</span>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search urgency" />
          </template>
        </Column>
        
        <Column field="current_status" header="Status" sortable>
          <template #body="{ data }">
            <div class="status-info">
              <span class="status-badge" :class="getAutoStatusClass(data)">
                {{ getAutoStatusText(data) }}
              </span>
              <div class="status-details">
                {{ getAutoStatusDetails(data) }}
              </div>
            </div>
          </template>
        </Column>
        
        <Column header="Actions" :exportable="false">
          <template #body="{ data }">
            <div class="actions">
              <Button 
                icon="pi pi-eye" 
                severity="secondary" 
                size="small"
                @click="viewSubject(data.id)" 
              />
              <Button 
                icon="pi pi-pencil" 
                severity="info" 
                size="small"
                @click="editSubject(data.id)" 
              />
              <Button 
                icon="pi pi-trash" 
                severity="danger" 
                size="small"
                @click="deleteSubject(data.id)" 
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Swal from 'sweetalert2'
import api from '../axios.js'

const router = useRouter()
const bills = ref([])
const loading = ref(false)

const filters = ref({
  global: { value: null, matchMode: 'contains' },
  subject_title: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  period_of_congress: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  tier: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  urgency: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] }
})

const filteredBills = computed(() => {
  return bills.value
})

const loadBills = async () => {
  try {
    loading.value = true
    const response = await api.get('/bills-by-subject')
    bills.value = response.data
  } catch (error) {
    console.error('Error loading bills:', error)
    bills.value = []
  } finally {
    loading.value = false
  }
}

const viewSubject = (id) => {
  router.push(`/subject/view/${id}`)
}

const editSubject = (id) => {
  router.push(`/subject/edit/${id}`)
}

const deleteSubject = async (id) => {
  const result = await Swal.fire({
    title: 'Delete Subject',
    text: 'Are you sure you want to delete this subject?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
    target: document.body
  })
  
  if (result.isConfirmed) {
    try {
      await api.delete(`/bills-by-subject/${id}`)
      loadBills()
      
      Swal.fire({
        title: 'Deleted!',
        text: 'Subject has been deleted successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        target: document.body
      })
    } catch (error) {
      console.error('Error deleting subject:', error)
    }
  }
}

const getUrgencyClass = (urgency) => {
  const classes = {
    'High': 'high',
    'Medium': 'medium',
    'Low': 'low'
  }
  return classes[urgency] || 'default'
}

const getSubjectTypeDisplay = (subjectType) => {
  if (Array.isArray(subjectType)) {
    return subjectType.join(', ')
  }
  return subjectType || 'N/A'
}

const getTierClass = (tier) => {
  if (tier === 'Tax') {
    return 'tax'
  } else if (tier === 'Non Tax') {
    return 'non-tax'
  } else if (tier === 'Both') {
    return 'both'
  }
  return 'tier-default'
}

const getAutoStatusClass = (bill) => {
  return `status-${bill.current_status || 'draft'}`
}

const getAutoStatusText = (bill) => {
  const stageNames = {
    'enactment': 'Enacted',
    'enrollment': 'Enrolled', 
    'bicam': 'Bicameral',
    'senate': 'Senate',
    'house': 'House',
    'draft': 'Draft'
  }
  return stageNames[bill.current_status] || 'Not Started'
}

const getAutoStatusDetails = (bill) => {
  return bill.status_details || 'No activity'
}

onMounted(() => {
  loadBills()
})
</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100vh;
}

.container {
  /* background: rgba(255, 255, 255, 0.95); */
  backdrop-filter: blur(10px);
  border-radius: 20px;
  margin: 1rem;
  padding: 1.5rem;
  /* box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1); */
  width: calc(100vw - 2rem);
  max-width: none;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #1e3a8a;
  margin: 0;
}

.add-btn {
  background: #1e3a8a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.add-btn:hover {
  background: #1e40af;
}

.btn-icon {
  font-size: 1.2rem;
  font-weight: bold;
}



.tier-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.tier-badge.tax {
  background: #fee2e2;
  color: #dc2626;
}

.tier-badge.non-tax {
  background: #e0e7ff;
  color: #3730a3;
}

.tier-badge.both {
  background: #fef3c7;
  color: #d97706;
}

.tier-badge.tier-default {
  background: #f3f4f6;
  color: #374151;
}

.urgency-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.urgency-badge.high {
  background: #fee2e2;
  color: #dc2626;
}

.urgency-badge.medium {
  background: #fef3c7;
  color: #d97706;
}

.urgency-badge.low {
  background: #d1fae5;
  color: #065f46;
}

.urgency-badge.default {
  background: #f3f4f6;
  color: #374151;
}

.status-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.status-draft {
  background: #f3f4f6;
  color: #6b7280;
}

.status-badge.status-house {
  background: #dbeafe;
  color: #1e40af;
}

.status-badge.status-senate {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge.status-bicam {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.status-enrollment {
  background: #e0e7ff;
  color: #3730a3;
}

.status-badge.status-enactment {
  background: #d1fae5;
  color: #065f46;
}

.status-details {
  font-size: 0.7rem;
  color: #6b7280;
  font-style: italic;
  margin-top: 0.25rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .page {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
}
</style>